import type { ApiException } from '@/common/error';

import { type CoOwnerInvite, type CoOwnerInviteDto, OWNERSHIP_ERROR } from './ownership.dto';

export const mapCoOwnerInvite = ({
  id,
  name,
  contact,
  message,
  created_at,
}: CoOwnerInviteDto): CoOwnerInvite => ({
  id,
  name,
  contact: contact ?? '',
  message: message || undefined,
  createdAt: created_at,
});

const MESSAGES: Record<string, string> = {
  [OWNERSHIP_ERROR.ALREADY_ON_VESSEL]:
    'This person is already on the vessel. Please change their role instead.',
  [OWNERSHIP_ERROR.NOT_ENOUGH_MANAGEMENT_SEATS]:
    'Every co-owner needs a management seat. Please add one to the plan first.',
  [OWNERSHIP_ERROR.SEATS_BELOW_OWNER_GROUP]:
    'The plan needs a management seat for the owner and every co-owner. Please keep one for each.',
  [OWNERSHIP_ERROR.CO_OWNER_REQUIRES_OWNER]:
    'Only a vessel with an owner can have co-owners. Please add the owner first.',
  [OWNERSHIP_ERROR.INVITER_NOT_ENTITLED]:
    'The sender can no longer add co-owners, so this invitation has lapsed. Please ask the owner for a new one.',
  [OWNERSHIP_ERROR.ONLY_OWNER_MANAGES_CO_OWNERS]:
    'Only the owner can change or remove a co-owner. Please ask the owner.',
  [OWNERSHIP_ERROR.ONLY_OWNER_CAN_TRANSFER]:
    'Only the owner can hand ownership over. Please ask the owner.',
  [OWNERSHIP_ERROR.TRANSFER_TARGET_NOT_CO_OWNER]:
    'Ownership can only go to a co-owner of this vessel. Please make them a co-owner first.',
  [OWNERSHIP_ERROR.OWNER_MUST_LEAVE]:
    'You can’t remove the owner. The owner can leave the vessel instead.',
  [OWNERSHIP_ERROR.OWNER_MUST_DELETE_VESSEL]:
    'Nobody would be left to run the vessel. Please add a co-owner or a captain first, or delete the vessel.',
  [OWNERSHIP_ERROR.OWNER_MISMATCH]:
    'The owner changed in the meantime. Please reload and try again.',
};

/**
 * The sentence to show for a refused invite, transfer, leave or removal, falling back to the
 * caller's "Couldn't …" when the refusal is not one of the multi-owner rules.
 */
export const ownershipErrorMessage = (error: unknown, fallback: string): string => {
  const type = (error as Partial<ApiException> | null)?.type;
  return (type && MESSAGES[type]) || fallback;
};
